import { PopoverPlacement } from "@idui/react-popover";
import { PhoneForwarded as PhoneForwardedIcon } from "@mui/icons-material";
import { FormControl, FormHelperText } from "@mui/material";
import clsx from "clsx";
import { isValidPhoneNumber } from "libphonenumber-js/max";
import {
  MuiTelInput,
  type MuiTelInputCountry,
  type MuiTelInputInfo,
} from "mui-tel-input";
import { useEffect, useId, useMemo, useState, type FC } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useDynamicFormsTranslate, useRtl } from "@ryde/redux";
import {
  callToClickHandler,
  checkCleanupNestedValidation,
  formatPhoneLocal,
  ValidationHelpers,
  type ICustomField,
} from "@ryde/shared";

import { FramerTooltip } from "../../common";
import { FieldExplanationIcon } from "./_parts";
import { StyledPhoneTemplate } from "./_template-styles";

const DEFAULT_COUNTRY: MuiTelInputCountry = "IL";

export const PhoneTemplate: FC<ICustomField> = ({
  id,
  name,
  formLabel,
  formAttributes,
  formValidation,
  e2eData = "phone-template",
  uniqueAttrId,
}) => {
  const {
    control,
    getValues,
    setValue,
    setError,
    formState: { errors },
  } = useFormContext();
  const generatedId = useId();
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
  });
  const { direction } = useRtl();
  const { t, i18n } = useTranslation();
  const { disabled, disablePhoneCallable } = formAttributes || {};

  const tel = getValues(name);
  const [unformattedValue, setUnformattedValue] = useState("");

  useEffect(() => {
    if (tel) {
      setUnformattedValue(tel?.[0] === "+" ? tel : `+${tel}`);
    }
  }, [tel]);

  const handleOnChange = (value: string, info: MuiTelInputInfo): void => {
    const prefix = `+${info.countryCallingCode}`;

    if (info.numberValue === prefix) {
      setValue(name, "", { shouldDirty: true, shouldValidate: true });
    } else {
      checkCleanupNestedValidation(name, errors);
      const isValid = isValidPhoneNumber(info.numberValue || prefix);

      if (isValid) {
        const newValue =
          info.numberValue?.[0] === "+"
            ? info.numberValue.substring(1)
            : info.numberValue;
        setValue(name, newValue, {
          shouldDirty: true,
          shouldValidate: true,
        });
      } else {
        setError(name, { type: "validate", message: t("errors.invalidPhone") });

        if (!info.numberValue) {
          setValue(name, "", { shouldDirty: true, shouldValidate: true });
        }
      }
    }

    setUnformattedValue(value);
  };

  const phoneMessage = t("general.PHONE_CALL");

  const requiredComputed = useMemo(() => {
    return ValidationHelpers.hasRequiredRule(formValidation);
  }, [formValidation]);

  const inputId = uniqueAttrId ?? `${name}-${generatedId}`;

  return (
    <StyledPhoneTemplate>
      <Controller
        name={name}
        control={control}
        render={({ field: { ref, ...field }, fieldState: { error } }) => {
          return (
            <FormControl error={!!error} fullWidth>
              <MuiTelInput
                id={inputId}
                dir={"ltr"}
                preferredCountries={[DEFAULT_COUNTRY]}
                defaultCountry={DEFAULT_COUNTRY}
                langOfCountryName={i18n.language}
                data-e2e={e2eData}
                data-e2e-field-name={name}
                data-e2e-id={`${name}_${id || ""}`}
                label={formLabelTranslated}
                required={requiredComputed}
                error={!!error}
                fullWidth
                focusOnSelectCountry
                forceCallingCode
                inputRef={ref}
                {...field}
                onChange={handleOnChange}
                value={unformattedValue}
                disabled={disabled}
                MenuProps={{
                  sx: {
                    ["& .MuiTelInput-Typography-calling-code"]: {
                      unicodeBidi: "plaintext",
                    },
                  },
                }}
              />

              {error && (
                <FormHelperText dir={direction}>
                  {error.message || t("errors.invalidPhone")}
                </FormHelperText>
              )}
            </FormControl>
          );
        }}
      />

      {!disablePhoneCallable && (
        <FramerTooltip
          content={`${phoneMessage} ${formatPhoneLocal(unformattedValue)}`}
          placement={PopoverPlacement.bottom}
        >
          <PhoneForwardedIcon
            className={clsx("phoneIcon", {
              phoneIconActive: unformattedValue,
              phoneIconDisabled: !unformattedValue,
            })}
            fontSize={"medium"}
            onClick={() => callToClickHandler(unformattedValue)}
          />
        </FramerTooltip>
      )}
      <FieldExplanationIcon formAttributes={formAttributes} />
    </StyledPhoneTemplate>
  );
};
